import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smart-leads`;
const err = (e, f) => toast.error(e?.response?.data?.detail || f);

// Shared Smart Lead Follow-Up state + actions (AI ranking map, outbox, replies, sends).
export function useSmartLeads() {
  const [ai, setAi] = useState({});
  const [meta, setMeta] = useState({ live: false, auto_send: false, needs_run: false });
  const [outbox, setOutbox] = useState([]);
  const [busy, setBusy] = useState("");
  const polls = useRef([]);

  const refresh = useCallback(async () => {
    try {
      const [q, o] = await Promise.all([axios.get(`${API}/queue`), axios.get(`${API}/outbox`)]);
      setAi(Object.fromEntries((q.data.items || []).map((i) => [i.lead_id, i])));
      setMeta({ live: q.data.live, auto_send: q.data.auto_send, needs_run: q.data.needs_run,
        computed_at: q.data.computed_at, last_reply_check_at: q.data.last_reply_check_at,
        last_reply_check_summary: q.data.last_reply_check_summary });
      setOutbox(o.data.items || []);
    } catch (e) { /* smart follow-up optional for this role/location */ }
  }, []);

  useEffect(() => { refresh(); return () => polls.current.forEach(clearInterval); }, [refresh]);

  const pollRun = (runId, onDone) => {
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/send-runs/${runId}`);
        if (data.status === "running") return;
        clearInterval(t);
        if (data.sent) toast.success(`Sent ${data.sent} text${data.sent > 1 ? "s" : ""} via Gingr`);
        if (data.replied_skipped) toast.info(`${data.replied_skipped} skipped — lead replied, follow-ups paused`);
        if (data.failed) toast.error(`${data.failed} failed${data.error ? ` — ${data.error}` : ""}. See Command Center.`);
        await refresh();
        onDone?.(data);
      } catch (e) { clearInterval(t); }
    }, 4000);
    polls.current.push(t);
  };

  const handleRun = (run, onDone) => {
    if (run.skipped?.length) toast.warning(`${run.skipped.length} skipped: ${run.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}`);
    if (run.mode !== "live") {
      if (run.would_send) toast.info(`Dry run — ${run.would_send} would send via Gingr. Nothing sent; arm Live in Agents → Smart Lead Follow-Up.`);
      return refresh();
    }
    if (run.would_send) { toast.info(`Sending ${run.would_send} via Gingr…`); pollRun(run.run_id, onDone); }
    return refresh();
  };

  const act = async (key, fn, fail) => {
    setBusy(key);
    try { return await fn(); } catch (e) { err(e, fail); return null; } finally { setBusy(""); }
  };

  return {
    ai, meta, outbox, busy, refresh,
    pending: outbox.filter((o) => o.status === "pending").length,
    getDraft: (lead_id, regenerate = false, note = "", channel = "sms") =>
      act("draft", async () => (await axios.post(`${API}/draft`, { lead_id, regenerate, note, channel })).data, "Draft failed"),
    saveDraft: (lead_id, text) =>
      act("save", async () => { const r = (await axios.put(`${API}/draft`, { lead_id, text })).data; toast.success("Draft saved"); return r; }, "Could not save draft"),
    saveEmailDraft: (lead_id, subject, body) =>
      act("save", async () => { const r = (await axios.put(`${API}/draft`, { lead_id, channel: "email", subject, body })).data; toast.success("Email draft saved"); return r; }, "Could not save email draft"),
    addToOutbox: (lead_id, message) =>
      act("outbox", async () => { await axios.post(`${API}/outbox`, { lead_id, message }); await refresh(); return true; }, "Could not add to outbox"),
    removeFromOutbox: (lead_id) =>
      act("outbox", async () => { await axios.delete(`${API}/outbox/${lead_id}`); await refresh(); return true; }, "Could not remove"),
    sendOutbox: (onDone) =>
      act("send", async () => handleRun((await axios.post(`${API}/outbox/send`)).data, onDone), "Send failed"),
    sendNow: (lead_id, message, onDone) =>
      act("send", async () => handleRun((await axios.post(`${API}/send`, { items: [{ lead_id, message }] })).data, onDone), "Send failed"),
    sendMany: (items, onDone) =>
      act("send", async () => handleRun((await axios.post(`${API}/send`, { items })).data, onDone), "Send failed"),
    batchDrafts: async (lead_ids) => (await axios.post(`${API}/drafts/batch`, { lead_ids })).data.items || [],
    logAttempt: (lead_id, channel, message = "") =>
      act("log", async () => {
        const r = (await axios.post(`${API}/log-attempt`, { lead_id, channel, message })).data;
        toast.success(r.closed ? "Logged — lead auto-closed as Lost (max attempts)" : `Logged ${channel} — now ${r.cadence_day}`);
        await refresh(); return r;
      }, "Could not log attempt"),
    markReplied: (lead_id, note = "") =>
      act("reply", async () => { await axios.post(`${API}/mark-replied`, { lead_id, note }); toast.success("Marked replied — follow-ups paused"); await refresh(); return true; }, "Could not mark replied"),
    resume: (lead_id) =>
      act("reply", async () => { await axios.post(`${API}/resume`, { lead_id }); toast.success("Follow-ups resumed"); await refresh(); return true; }, "Could not resume"),
    rerank: () =>
      act("rank", async () => { const r = (await axios.post(`${API}/run`)).data; toast.success(`AI ranked ${r.ranked} leads`); await refresh(); return r; }, "Ranking failed"),
    checkReplies: () =>
      act("check", async () => {
        const r = (await axios.post(`${API}/check-replies`)).data;
        if (r.error) toast.error(`Reply check: ${r.error}`);
        else toast.success(r.replied.length ? `${r.replied.length} replied: ${r.replied.map((x) => x.name).join(", ")} — paused` : `No new replies (${r.checked} checked)`);
        await refresh(); return r;
      }, "Reply check failed"),
  };
}
